import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, MessageSquare } from "lucide-react";
import { z } from "zod";
import { useSendOTP, useSession, useVerifyOTP } from "@empite/session";
import type { OTPChallenge } from "@empite/api-client";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { AuthShell } from "../ui/auth-shell";
import { Button } from "../ui/button";
import { Notice } from "../ui/notice";
import { OtpInput } from "../ui/otp-input";
import { UiLink } from "../ui/router";
import { errorMessage } from "./guards";
import { safeReturnPath } from "../oauth";

const schema = z.object({
  code: z.string().regex(/^\d{4,8}$/, "Enter the code from the message."),
});

type Values = z.infer<typeof schema>;

/**
 * The phone verification screen.
 *
 * The user_id comes from the URL, not from a session: registration does not
 * sign anyone in, so this screen has to work for a visitor with no cookie at
 * all. It also works for a signed-in customer whose phone is still pending,
 * which is why the session is used only as a fallback.
 */
export interface VerifyPhoneSearch {
  /** Whose phone. Present when the visitor arrived from registration and
   *  has no session at all yet. */
  user_id?: string;
  next?: string;
}

const panel = {
  headline: "One code, once — then every application in the suite trusts this device.",
  sub: "Your number is verified a single time and never shown to an application.",
  bullets: [
    "The code is good for a few minutes and a handful of attempts",
    "Verifying protects the wallet behind every application",
    "You will not be asked for it again on this account",
  ],
};

export function VerifyPhone({ search }: { search: VerifyPhoneSearch }) {
  const navigate = useNavigate();
  const session = useSession();
  const userID = search.user_id ?? session.data?.user_id;

  const sendOTP = useSendOTP();
  const verifyOTP = useVerifyOTP();
  const [challenge, setChallenge] = useState<OTPChallenge | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [verified, setVerified] = useState(false);
  const [formError, setFormError] = useState<unknown>(null);
  const form = useZodForm<Values>(schema, { defaultValues: { code: "" } });
  // The boxes are a controlled input, so the code is watched out of form state
  // rather than registered as a native field.
  const code = form.watch("code") ?? "";

  // The server decides when a resend is allowed; this only counts down what
  // it said, so the button cannot invite a request that will be refused.
  useEffect(() => {
    if (resendIn <= 0) {
      return;
    }
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  if (!userID) {
    return (
      <AuthShell
        title="Nothing to verify"
        subtitle="This link carries no account, and there is no session to fall back on."
        panel={panel}
      >
        <div className="mt-8 space-y-4">
          <Notice
            tone="warning"
            title="Start again"
            icon={<AlertCircle className="size-4" aria-hidden />}
          >
            Create an account, or sign in first and we will pick the verification up
            from there.
          </Notice>
          <Button href="/register" size="lg" className="w-full">
            Create an account
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </div>
      </AuthShell>
    );
  }

  const send = async () => {
    setFormError(null);
    try {
      const issued = await sendOTP.mutateAsync(userID);
      setChallenge(issued);
      setResendIn(issued.resend_available_in_seconds);
    } catch (err) {
      setFormError(err);
    }
  };

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await verifyOTP.mutateAsync({ user_id: userID, code: values.code });
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["code"])) {
        setFormError(err);
      }
      return;
    }
    setVerified(true);
    const next = safeReturnPath(search.next);
    if (next) {
      window.location.assign(next);
      return;
    }
    // Verifying does not create a browser session, so a visitor who arrived
    // straight from registration still has to sign in.
    if (!session.data) {
      await navigate({ to: "/sign-in", search: { next: undefined, client_id: undefined } });
      return;
    }
    await navigate({ to: "/" });
  });

  const masked = challenge?.phone_last_digits
    ? `${challenge.phone_country_code ?? ""} ••• ${challenge.phone_last_digits}`
    : undefined;

  return (
    <AuthShell
      title="Verify your phone"
      subtitle={
        challenge
          ? `We sent a one-time code${masked ? ` to ${masked}` : ""}. Enter it below.`
          : "We send a one-time code by SMS. It is needed once, before you can activate an application."
      }
      panel={panel}
      footer={
        <p className="text-center text-sm text-ink-3">
          Wrong account?{" "}
          <UiLink
            href="/sign-in"
            search={{ next: undefined, client_id: undefined }}
            className="font-semibold text-brand hover:text-brand-strong"
          >
            Sign in as someone else
          </UiLink>
          .
        </p>
      }
    >
      <div className="mt-8 space-y-4">
        {formError ? (
          <Notice
            tone="danger"
            title="Verification failed"
            icon={<AlertCircle className="size-4" aria-hidden />}
          >
            {errorMessage(formError)}
          </Notice>
        ) : null}

        {verified ? (
          <Notice
            tone="success"
            title="Your phone is verified"
            icon={<CheckCircle2 className="size-4" aria-hidden />}
          >
            Taking you on now.
          </Notice>
        ) : null}

        {challenge ? (
          <>
            {/* dev_code is present only in a local build, where no SMS
                provider exists to carry the code anywhere. */}
            {challenge.dev_code ? (
              <Notice
                tone="info"
                title="Local build"
                icon={<MessageSquare className="size-4" aria-hidden />}
              >
                No SMS provider is configured, so the code is{" "}
                <strong className="font-display font-bold tabular-nums text-ink">
                  {challenge.dev_code}
                </strong>
                .
              </Notice>
            ) : null}

            <form onSubmit={submit} noValidate className="space-y-5">
              <OtpInput
                label="One-time code"
                autoFocus
                value={code}
                onChange={(next) => {
                  form.clearErrors("code");
                  form.setValue("code", next, { shouldDirty: true });
                }}
                error={form.formState.errors.code?.message}
                hint={`Expires in ${Math.round(
                  challenge.expires_in_seconds / 60,
                )} minutes, and may be attempted ${challenge.max_attempts} times.`}
              />

              <Button type="submit" size="lg" className="w-full" disabled={verifyOTP.isPending}>
                {verifyOTP.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify my phone
                    <ArrowRight className="size-4" aria-hidden />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-ink-3">
                Nothing arrived?{" "}
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={resendIn > 0 || sendOTP.isPending}
                  className="font-semibold text-brand transition-colors hover:text-brand-strong disabled:cursor-default disabled:text-ink-4 disabled:hover:text-ink-4"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Send it again"}
                </button>
              </p>
            </form>
          </>
        ) : (
          <>
            <Notice
              tone="info"
              title="Send yourself a code"
              icon={<MessageSquare className="size-4" aria-hidden />}
            >
              We will text a six-digit code to the number on the account. It is only
              needed this once.
            </Notice>
            <Button
              size="lg"
              className="w-full"
              onClick={() => void send()}
              disabled={sendOTP.isPending}
            >
              {sendOTP.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Sending…
                </>
              ) : (
                <>
                  Send code
                  <ArrowRight className="size-4" aria-hidden />
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </AuthShell>
  );
}
